import { JsonSchema } from '@jsonforms/core';
import AJV, { ErrorObject, JSONSchemaType } from 'ajv';
import { useState } from 'react';

export type ValidationError = {
  field: string;
  instancePath: string;
  message: string;
  keyword: string;
};
export type ValidateParams<T> = {
  data: Partial<T>;
  prop?: 'instancePath' | 'field';
  value?: string;
};
type ConstructErrorsParams = {
  ajvErrors: ErrorObject[];
  prop?: keyof ValidationError;
  value?: string;
  prependPath?: string;
};
export type FormValidationHookResult<T> = {
  valid: boolean;
  errors: ValidationError[];
  validate: (
    params: ValidateParams<T>,
    subSchema?: any,
    dataPath?: string,
  ) => { valid: boolean; errors: ValidationError[] };
  resetErrors: () => void;
};

export const useValidation = <T,>(
  validationSchema: JSONSchemaType<any> | JsonSchema,
  ajv: AJV,
  uiSchema?: JsonSchema
): FormValidationHookResult<T> => {
  const [valid, setValid] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  let ajvValidate = ajv.compile(validationSchema);

  const resetErrors = () => {
    setErrors([]);
  };

  const validate = (
    { data, prop, value }: ValidateParams<T>,
    subSchema?: any,
    prependPath?: string,
  ) => {
    if (subSchema) ajvValidate = ajv.compile(subSchema);
    let valid = ajvValidate(data);
    setValid(valid);
    let newErrors = constructErrors({
      ajvErrors: ajvValidate.errors as ErrorObject[],
      prop,
      value,
      prependPath,
    });
    setErrors(newErrors);

    return { valid, errors: newErrors };
  };

  const constructErrors = ({
    ajvErrors,
    prop,
    value,
    prependPath = '',
  }: ConstructErrorsParams): ValidationError[] => {
    const mappedErrors = ajvErrors?.map((current: ErrorObject) => {
      let fieldName = '';
      if (current.keyword === 'required') {
        fieldName = current.params.missingProperty;
        current.instancePath += `/${fieldName}`;
        //@ts-ignore
        current.message = uiSchema?.options?.requiredMessage ? uiSchema?.options?.requiredMessage :  `This field is required.`;
      } else {
        let instancePathParts = current.instancePath.split('/');
        fieldName = instancePathParts[instancePathParts.length - 1];
      }

      return {
        instancePath: `${prependPath}${current.instancePath}`,
        message: current.message,
        keyword: current.keyword,
        field: fieldName,
      } as ValidationError;
    });

    if (prop && value)
      return mappedErrors?.filter((item: ValidationError) => {
        return (
          item[prop] === value ||
          errors?.find(
            (stateError: ValidationError) => stateError[prop] === item[prop],
          )
        );
      });
    return mappedErrors || [];
  };

  return {
    valid,
    errors,
    validate,
    resetErrors,
  };
};
